import {Trans} from '@lingui/macro';
import React, {useCallback, useMemo, useState} from 'react';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {buildVisualRecipientForEmail} from '../../helpers/models/recipientParsers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {RecipientRolesEnum} from '../../models/api/recipientModel';
import {
  SettingsAdminHomeUrl,
  SettingsRootPagesEnum,
  SettingsUserHomeUrl,
} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {getUserOwnedAnswers} from '../../store/data/answers/answersSelectors';
import {
  getIsBlocked,
  getIsFreePlan,
  makeGetCanCompanyAddTeammates,
  makeGetCompanyMaxTeammates,
} from '../../store/data/company/companySelectors';
import {getUserOwnedInboxes} from '../../store/data/inboxes/inboxesSelectors';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {getUserOwnedSignatures} from '../../store/data/signatures/signaturesSelectors';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf, getTeamsUserIsAdminOf} from '../../store/data/teams/teamsSelectors';
import {getUserOwnedResponders} from '../../store/data/vacationResponders/vacationRespondersSelectors';
import {getHasNewTeammateInvite} from '../../store/featureFlags/featureFlagsSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {getBillingContactEmail} from '../../store/stateless/welcomeCarouselSelectors';
import {makeSettingsUrlFromPathId, SettingsPathId} from '../menu/settingsHelpers';
import {SettingsHomeNamespaceSelectDialog} from './settingsHomeNamespaceSelectDialog';
import {NamespaceSelectResourceTypesEnum, SettingsHomeDestinationsEnum} from './settingsHomeTypes';

type QuickAccessParams = {
  namespace?: string;
  inbox?: number;
  id?: number;
  resource?: string;
  action?: string;
  adminHome?: boolean;
  visualSettings?: string;
  answer?: number;
  signature?: number;
};

interface NamespaceSelectState {
  isCreating: boolean;
  resourceType?: NamespaceSelectResourceTypesEnum;
  destination?: SettingsHomeDestinationsEnum;
}

type DestinationHandler = HandlerOf<SettingsHomeDestinationsEnum>;
type MethodForDestinationMap = {[K in SettingsHomeDestinationsEnum]: DestinationHandler};

export function useHomeShortcutClick(
  settingsHomeUrl: SettingsUserHomeUrl | SettingsAdminHomeUrl,
  panelId: SettingsPanelId,
) {
  const isAdmin = settingsHomeUrl.rootPage === SettingsRootPagesEnum.ADMIN_HOME;
  const dispatch = useAppDispatch();
  const userNamespace = useAppSelector(getUserNamespace);
  const [isOnFreePlanDialogOpen, setIsOnFreePlanDialogOpen] = useState(false);
  const [isCannotAddMoreTeammatesDialogOpen, setIsCannotAddMoreTeammatesDialogOpen] = useState(false);
  const [namespaceSelectState, setNamespaceSelectState] = useState<NamespaceSelectState>({
    isCreating: false,
  });

  const goToNewSettingsUrl = useCallback(
    (path: SettingsPathId, params: QuickAccessParams) => {
      const newUrl = makeSettingsUrlFromPathId(path, params);
      dispatch(navigateToLinkAction(newUrl));
    },
    [dispatch],
  );

  const goAndReportQuickAccessClick = useCallback(
    (path: SettingsPathId, params: QuickAccessParams) => {
      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_HOMEPAGE_CLICK, {
        click_type: 'settings',
        click_object: path,
        namespace: params.namespace,
        home_type: isAdmin ? 'admin' : 'user',
      });
      goToNewSettingsUrl(path, params);
    },
    [goToNewSettingsUrl, isAdmin],
  );

  const hasNewTeammateInvite = useAppSelector(getHasNewTeammateInvite);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const firstTeam = useAppSelector(getFirstTeamUserIsAdminOf);
  const teamsUserIsAdminOf = useAppSelector(getTeamsUserIsAdminOf);
  const hasSingleTeam = teamsUserIsAdminOf.length === 1;
  const responders = useAppSelector(getUserOwnedResponders);
  const privateInboxes = useAppSelector(getUserOwnedInboxes);
  const userOwnedAnswers = useAppSelector(getUserOwnedAnswers);
  const isCompanyBlocked = useAppSelector(getIsBlocked);
  const isCompanyFree = useAppSelector(getIsFreePlan);
  const getCompanyMaxTeammates = useMemo(() => makeGetCompanyMaxTeammates(() => false), []);
  const companyMaxTeammates = useAppSelector(getCompanyMaxTeammates);
  const getCanCompanyAddTeammates = useMemo(
    () => makeGetCanCompanyAddTeammates(() => 1, getCompanyMaxTeammates),
    [getCompanyMaxTeammates],
  );
  const canCompanyAddTeammates = useAppSelector(getCanCompanyAddTeammates);
  const billingHelperEmail = useAppSelector(getBillingContactEmail);

  const openCreatePrivateInbox: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.inboxes.new', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);

  const openEditPrivateInbox: DestinationHandler = useCallback(() => {
    const firstPrivate = privateInboxes[0];
    if (!firstPrivate) {
      return;
    }

    goAndReportQuickAccessClick('settings.inboxes.edit.overview', {
      namespace: userNamespace,
      inbox: firstPrivate.id,
    });
  }, [goAndReportQuickAccessClick, privateInboxes, userNamespace]);
  const openReviewPrivateInboxes: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.inboxes', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const openCreateResponder: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.responder.new', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const openReviewResponders: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.responder.list', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);

  const openEditResponder: DestinationHandler = useCallback(() => {
    const firstResponder = responders[0];

    if (!firstResponder) {
      return;
    }

    goAndReportQuickAccessClick('settings.responder.edit', {
      namespace: userNamespace,
      id: firstResponder.id,
    });
  }, [goAndReportQuickAccessClick, responders, userNamespace]);
  const openCreateAnswer: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.answers.new', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const openReviewAnswers: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.answers.list', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const openEditAnswer: DestinationHandler = useCallback(() => {
    const firstAnswer = userOwnedAnswers[0];
    if (!firstAnswer) {
      return;
    }

    goAndReportQuickAccessClick('settings.answers.edit.overview', {
      namespace: userNamespace,
      answer: firstAnswer.id,
    });
  }, [goAndReportQuickAccessClick, userNamespace, userOwnedAnswers]);
  const openEditPreferences: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.myPreferences.general', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const openCreateSignature: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.signatures.new', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);
  const userOwnedSignatures = useAppSelector(getUserOwnedSignatures);
  const openEditSignature: DestinationHandler = useCallback(() => {
    const firstSignature = userOwnedSignatures[0];

    if (!firstSignature) {
      return;
    }

    goAndReportQuickAccessClick('settings.signatures.edit.overview', {
      namespace: userNamespace,
      signature: firstSignature.id,
    });
  }, [goAndReportQuickAccessClick, userNamespace, userOwnedSignatures]);
  const openReviewSignatures: DestinationHandler = useCallback(() => {
    goAndReportQuickAccessClick('settings.signatures', {namespace: userNamespace});
  }, [goAndReportQuickAccessClick, userNamespace]);

  const checkPlan = useCallback(() => {
    if (isCompanyBlocked) {
      return false;
    }

    if (isCompanyFree) {
      setIsOnFreePlanDialogOpen(true);

      return false;
    }

    if (!canCompanyAddTeammates) {
      setIsCannotAddMoreTeammatesDialogOpen(true);

      return false;
    }

    return true;
  }, [
    canCompanyAddTeammates,
    isCompanyBlocked,
    isCompanyFree,
    setIsCannotAddMoreTeammatesDialogOpen,
    setIsOnFreePlanDialogOpen,
  ]);
  const openAddTeammates: DestinationHandler = useCallback(() => {
    const newTeammatesPath = hasNewTeammateInvite ? 'settings.teammates.new-ui' : 'settings.teammates.new';

    if (!checkPlan()) {
      return;
    }

    if (!firstTeam) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_INVITE_TEAMMATE_CLICK, {
      namespace_type: 'global',
    });

    // If the user is a global admin, take them to the global invite page.
    if (isGlobalAdmin) {
      goAndReportQuickAccessClick(newTeammatesPath, {namespace: 'global'});
      return;
    }

    // If the user is an admin of only one team, take them to the invite page for that team.
    if (hasSingleTeam) {
      goAndReportQuickAccessClick(newTeammatesPath, {namespace: firstTeam.namespace});
      return;
    }

    // Otherwise, they must be an admin of multiple teams, so let them choose.
    setNamespaceSelectState({
      resourceType: NamespaceSelectResourceTypesEnum.TEAMMATE,
      isCreating: true,
    });
  }, [checkPlan, firstTeam, goAndReportQuickAccessClick, hasNewTeammateInvite, hasSingleTeam, isGlobalAdmin]);
  const openReviewTeammates: DestinationHandler = useCallback(() => {
    if (!firstTeam) {
      return;
    }

    if (isGlobalAdmin) {
      goAndReportQuickAccessClick('settings.teammates.list', {namespace: 'global'});
      return;
    }

    if (hasSingleTeam) {
      goAndReportQuickAccessClick('settings.teammates.list', {namespace: firstTeam.namespace});
      return;
    }

    setNamespaceSelectState({
      resourceType: NamespaceSelectResourceTypesEnum.TEAMMATE,
      isCreating: false,
    });
  }, [firstTeam, goAndReportQuickAccessClick, hasSingleTeam, isGlobalAdmin]);
  const openCreateRule: DestinationHandler = useCallback(
    (destination) => {
      if (!firstTeam) {
        return;
      }

      if (hasSingleTeam) {
        goAndReportQuickAccessClick('settings.rules', {namespace: firstTeam.namespace});
        return;
      }

      setNamespaceSelectState({
        resourceType: NamespaceSelectResourceTypesEnum.RULES,
        isCreating: true,
        destination,
      });
    },
    [firstTeam, goAndReportQuickAccessClick, hasSingleTeam],
  );
  const openReviewRules: DestinationHandler = useCallback(
    (destination) => {
      if (!firstTeam) {
        return;
      }

      if (hasSingleTeam) {
        goAndReportQuickAccessClick('settings.rules', {namespace: firstTeam.namespace});
        return;
      }

      setNamespaceSelectState({
        resourceType: NamespaceSelectResourceTypesEnum.RULES,
        isCreating: false,
        destination,
      });
    },
    [firstTeam, goAndReportQuickAccessClick, hasSingleTeam],
  );
  const openReviewInboxes: DestinationHandler = useCallback(
    (destination) => {
      if (!firstTeam) {
        return;
      }

      if (hasSingleTeam) {
        goAndReportQuickAccessClick('settings.inboxes', {namespace: firstTeam.namespace});
        return;
      }

      setNamespaceSelectState({
        resourceType: NamespaceSelectResourceTypesEnum.INBOXES,
        isCreating: false,
        destination,
      });
    },
    [firstTeam, goAndReportQuickAccessClick, hasSingleTeam],
  );
  const openCreateInbox: DestinationHandler = useCallback(
    (destination) => {
      if (!firstTeam) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.ADD_INBOX_CLICKED, {
        visualSettings: 'shared',
        adminHome: true,
      });

      if (hasSingleTeam) {
        goAndReportQuickAccessClick('settings.inboxes.new', {namespace: firstTeam.namespace});
        return;
      }

      setNamespaceSelectState({
        resourceType: NamespaceSelectResourceTypesEnum.INBOXES,
        isCreating: true,
        destination,
      });
    },
    [firstTeam, goAndReportQuickAccessClick, hasSingleTeam],
  );
  const methodForDestinationMap: MethodForDestinationMap = useMemo(
    () => ({
      // Private actions.
      [SettingsHomeDestinationsEnum.ADD_PRIVATE_INBOX]: openCreatePrivateInbox,
      [SettingsHomeDestinationsEnum.EDIT_PRIVATE_INBOX]: openEditPrivateInbox,
      [SettingsHomeDestinationsEnum.REVIEW_PRIVATE_INBOXES]: openReviewPrivateInboxes,
      [SettingsHomeDestinationsEnum.ADD_RESPONDER]: openCreateResponder,
      [SettingsHomeDestinationsEnum.REVIEW_RESPONDERS]: openReviewResponders,
      [SettingsHomeDestinationsEnum.EDIT_RESPONDER]: openEditResponder,
      [SettingsHomeDestinationsEnum.ADD_ANSWER]: openCreateAnswer,
      [SettingsHomeDestinationsEnum.REVIEW_ANSWERS]: openReviewAnswers,
      [SettingsHomeDestinationsEnum.EDIT_ANSWER]: openEditAnswer,
      [SettingsHomeDestinationsEnum.ADD_SIGNATURE]: openCreateSignature,
      [SettingsHomeDestinationsEnum.EDIT_SIGNATURE]: openEditSignature,
      [SettingsHomeDestinationsEnum.REVIEW_SIGNATURES]: openReviewSignatures,
      [SettingsHomeDestinationsEnum.EDIT_PREFERENCES]: openEditPreferences,

      // Team actions.
      [SettingsHomeDestinationsEnum.ADD_TEAMMATES]: openAddTeammates,
      [SettingsHomeDestinationsEnum.REVIEW_TEAM_INBOXES]: openReviewInboxes,
      [SettingsHomeDestinationsEnum.ADD_TEAM_INBOX]: openCreateInbox,
      [SettingsHomeDestinationsEnum.REVIEW_TEAMMATES]: openReviewTeammates,
      [SettingsHomeDestinationsEnum.ADD_RULE]: openCreateRule,
      [SettingsHomeDestinationsEnum.REVIEW_RULES]: openReviewRules,
    }),
    [
      openAddTeammates,
      openCreateAnswer,
      openCreateInbox,
      openCreatePrivateInbox,
      openCreateResponder,
      openCreateRule,
      openCreateSignature,
      openEditAnswer,
      openEditPreferences,
      openEditPrivateInbox,
      openEditResponder,
      openEditSignature,
      openReviewAnswers,
      openReviewInboxes,
      openReviewPrivateInboxes,
      openReviewResponders,
      openReviewRules,
      openReviewSignatures,
      openReviewTeammates,
    ],
  );

  const onShortcutClick = useCallback(
    (destination: SettingsHomeDestinationsEnum) => {
      if (!methodForDestinationMap[destination]) {
        return;
      }

      methodForDestinationMap[destination](destination);
    },
    [methodForDestinationMap],
  );

  const onContactSalesClick = useCallback(() => {
    const supportEmailRecipient = buildVisualRecipientForEmail(RecipientRolesEnum.TO, billingHelperEmail);
    dispatch(
      composeMessageAction({
        recipients: [supportEmailRecipient],
        shouldForcePopout: true,
      }),
    );

    setIsCannotAddMoreTeammatesDialogOpen(false);
  }, [billingHelperEmail, dispatch]);

  const maybeRenderDialogs = () => {
    if (namespaceSelectState.resourceType) {
      return (
        <SettingsHomeNamespaceSelectDialog
          onRequestClose={() => {
            setNamespaceSelectState({
              resourceType: undefined,
              isCreating: false,
            });
          }}
          shouldShowOnlyTeams={
            namespaceSelectState.destination === SettingsHomeDestinationsEnum.ADD_TEAM_INBOX ||
            namespaceSelectState.destination === SettingsHomeDestinationsEnum.ADD_RULE ||
            namespaceSelectState.destination === SettingsHomeDestinationsEnum.REVIEW_RULES ||
            namespaceSelectState.destination === SettingsHomeDestinationsEnum.REVIEW_TEAM_INBOXES
          }
          isCreatingResource={namespaceSelectState.isCreating}
          resourceType={namespaceSelectState.resourceType}
          onTeamSelect={(namespace) => {
            const newPath = makeTargetPath(
              namespace,
              namespaceSelectState.isCreating,
              namespaceSelectState.resourceType!,
            );
            goToNewSettingsUrl(newPath, {namespace});
          }}
        />
      );
    }

    if (isOnFreePlanDialogOpen) {
      return (
        <OkCancelDialog
          title={<Trans id="homeshortcut-planexceeded">Plan exceeded</Trans>}
          okButtonLabel={<Trans id="homeshortcut-upgradenow">Upgrade now</Trans>}
          onOkClick={() => {
            goToNewSettingsUrl('settings.billing', {});
          }}
          onRequestClose={() => setIsOnFreePlanDialogOpen(false)}
        >
          <Trans id="homeshortcut-cannotaddmoreteammates">
            You cannot add more teammates while on the Free plan
          </Trans>
        </OkCancelDialog>
      );
    }

    if (isCannotAddMoreTeammatesDialogOpen) {
      return (
        <OkCancelDialog
          title={<Trans id="homeshortcut-planexceeded">Plan exceeded</Trans>}
          okButtonLabel={<Trans id="homeshortcut-contactsales">Contact Sales</Trans>}
          onOkClick={onContactSalesClick}
          onRequestClose={() => setIsCannotAddMoreTeammatesDialogOpen(false)}
        >
          <Trans id="homeshortcut-planonlysupports">
            Your plan only supports {companyMaxTeammates} teammates
          </Trans>
        </OkCancelDialog>
      );
    }

    return null;
  };

  return {onShortcutClick, maybeRenderDialogs};
}

const makeTargetPath = (
  namespace: string,
  isNew: boolean,
  resource: NamespaceSelectResourceTypesEnum,
): SettingsPathId => {
  if (resource === NamespaceSelectResourceTypesEnum.USER_PROVISIONING) {
    return 'settings.teammates.provisioning';
  }

  if (resource === NamespaceSelectResourceTypesEnum.ACTIVITY_EXPORT) {
    return 'settings.teammates.export';
  }

  if (resource === NamespaceSelectResourceTypesEnum.RULES) {
    if (isNew) {
      return namespace && isTeamNamespace(namespace) ? 'settings.rules.ui.library' : 'settings.rules.ui.new';
    }

    return 'settings.rules.ui';
  }
  if (resource === NamespaceSelectResourceTypesEnum.INBOXES) {
    if (isNew) {
      return 'settings.inboxes.new';
    }

    return 'settings.inboxes';
  }
  if (resource === NamespaceSelectResourceTypesEnum.SIGNATURES) {
    if (isNew) {
      return 'settings.signatures.new';
    }

    return 'settings.signatures';
  }
  if (resource === NamespaceSelectResourceTypesEnum.ANSWERS) {
    if (isNew) {
      return 'settings.answers.new';
    }

    return 'settings.answers';
  }
  if (resource === NamespaceSelectResourceTypesEnum.CUSTOM_VIEWS) {
    if (isNew) {
      return 'settings.custom-views.new';
    }

    return 'settings.custom-views';
  }
  if (resource === NamespaceSelectResourceTypesEnum.TAGS) {
    if (isNew) {
      return 'settings.tags.new';
    }

    return 'settings.tags';
  }
  if (resource === NamespaceSelectResourceTypesEnum.ROLES) {
    if (isNew) {
      return 'settings.roles.new';
    }

    return 'settings.roles';
  }
  if (resource === NamespaceSelectResourceTypesEnum.SHIFTS) {
    return 'settings.shifts';
  }

  if (resource === NamespaceSelectResourceTypesEnum.LOAD_BALANCING) {
    return 'settings.rules.ui.limits.list';
  }

  if (isNew) {
    return 'settings.teammates.new';
  }

  return 'settings.teammates.list';
};
